import { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem as SelectItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { MenuItem, MenuType } from '../../../interfaces/Menu/MenuItem';
import { createMenuItem, updateMenuItem } from '../../../api/MenuItem';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { findMenuItemInTree } from '../../../utils/Menu';
import { Category } from '../../../interfaces/Category/Category';
import { getCategories } from '../../../api/Category';
import { Page } from '../../../interfaces/Page/Page';
import { getPages } from '../../../api/Page';
import { TranslationAdornment } from '../../../components/Translation/TranslationAdornment';

interface ModifyMenuItemSectionProps {
  menuItem: MenuItem;
  menuItems: MenuItem[];
  onModify: (id: number) => void;
  onCancel: () => void;
}

export const ModifyMenuItemSection = (props: ModifyMenuItemSectionProps) => {
  const [currentState, setCurrentState] = useState(cloneDeep(props.menuItem));
  const [categories, setCategories] = useState<Category[]>([]);
  const [pages, setPages] = useState<Page[]>([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);

  const parent = useMemo(
    () =>
      Number.isFinite(currentState.parentID)
        ? findMenuItemInTree(props.menuItems, currentState.parentID!)
        : undefined,
    [props.menuItems, currentState.parentID],
  );

  useEffect(() => {
    setLoadingCategories(true);
    getCategories()
      .then((c) => setCategories(c))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingCategories(false));

    setLoadingPages(true);
    getPages()
      .then((p) => setPages(p))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingPages(false));
  }, []);

  useEffect(() => {
    setCurrentState(cloneDeep(props.menuItem));
  }, [props.menuItem]);

  const isEditing = !!currentState.id;

  const handleSave = () => {
    setLoadingMenu(true);

    const mutation = isEditing ? updateMenuItem : createMenuItem;
    mutation(currentState)
      .then((item) => {
        toast.success('Değişiklikler başarıyla kaydedildi');
        props.onModify(item.id);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingMenu(false));
  };

  const handleChangeType = useCallback(
    (newType: MenuType) => {
      switch (newType) {
        case MenuType.CATEGORY: {
          setCurrentState({
            ...currentState,
            type: newType,
            categoryID: categories[0]?.id,
            pageID: undefined,
            value: categories[0].slug ?? '',
          });
          break;
        }
        case MenuType.PAGE: {
          setCurrentState({
            ...currentState,
            type: newType,
            categoryID: undefined,
            pageID: pages[0]?.id,
            value: pages[0]?.slug ?? '',
          });
          break;
        }
        default: {
          setCurrentState({
            ...currentState,
            type: newType,
            categoryID: undefined,
            pageID: undefined,
            value: '/',
          });
          break;
        }
      }
    },
    [currentState],
  );

  const loading = loadingCategories || loadingMenu || loadingPages;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Waiting open={loading} />
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
          {isEditing ? 'Ögeyi Güncelle' : 'Yeni Öge'}
        </Typography>

        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={currentState.name}
          onChange={(e) =>
            setCurrentState({ ...currentState, name: e.target.value })
          }
          fullWidth
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                defaultValue={currentState.name}
                value={currentState.nameTranslation}
                onChange={(x) =>
                  setCurrentState({ ...currentState, nameTranslation: x })
                }
              />
            ),
          }}
        />

        {!!parent && (
          <TextField
            label='Üst Menü'
            variant='outlined'
            size='small'
            value={parent.name}
            disabled
            fullWidth
          />
        )}

        <FormControl fullWidth>
          <InputLabel>Menü Tipi</InputLabel>
          <Select
            label='Menü Tipi'
            size='small'
            value={currentState.type}
            onChange={(e) => handleChangeType(e.target.value as MenuType)}
          >
            <SelectItem value={MenuType.URL}>Link</SelectItem>
            <SelectItem value={MenuType.CATEGORY}>Kategori</SelectItem>
            <SelectItem value={MenuType.PAGE}>Sayfa</SelectItem>
          </Select>
        </FormControl>

        {currentState.type === MenuType.CATEGORY && (
          <FormControl fullWidth>
            <InputLabel>Kategori</InputLabel>
            <Select
              label='Kategori'
              size='small'
              value={currentState.categoryID ?? -1}
              onChange={(e) =>
                setCurrentState({
                  ...currentState,
                  categoryID: Number(e.target.value),
                  value:
                    categories.find(
                      (category) => category.id === Number(e.target.value),
                    )?.slug ?? '',
                })
              }
            >
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </Select>
          </FormControl>
        )}

        {currentState.type === MenuType.PAGE && (
          <FormControl fullWidth>
            <InputLabel>Sayfa</InputLabel>
            <Select
              label='Sayfa'
              size='small'
              value={currentState.pageID ?? -1}
              onChange={(e) =>
                setCurrentState({
                  ...currentState,
                  pageID: Number(e.target.value),
                  value:
                    pages.find((page) => page.id === Number(e.target.value))
                      ?.slug ?? '',
                })
              }
            >
              {pages.map((page) => (
                <SelectItem key={page.id} value={page.id}>
                  {page.name}
                </SelectItem>
              ))}
            </Select>
          </FormControl>
        )}

        {currentState.type === MenuType.URL && (
          <TextField
            label='URL'
            variant='outlined'
            size='small'
            value={currentState.value}
            onChange={(e) =>
              setCurrentState({ ...currentState, value: e.target.value })
            }
            fullWidth
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.isActive}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    isActive: !currentState.isActive,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.isFluid}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    isFluid: !currentState.isFluid,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Tam Boy</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.targetNewTab}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    targetNewTab: !currentState.targetNewTab,
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>Yeni Sekme</Typography>
            }
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          <Button
            color='inherit'
            variant='contained'
            onClick={props.onCancel}
            sx={{ ...sharedStyles.buttonText }}
          >
            İptal
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
